import { PieChart } from 'lucide-react';
import {
  ComponentType,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import authSelectors from 'src/modules/auth/authSelectors';
import PermissionChecker from 'src/modules/auth/permissionChecker';
import DataStudioService from 'src/modules/dataStudio/dataStudioService';
import layoutSelectors from 'src/modules/layout/layoutSelectors';
import Permissions from 'src/security/permissions';
import staticMenus from 'src/view/menus';
import CustomLoadable from 'src/view/shared/CustomLoadable';
import ProgressBar from 'src/view/shared/ProgressBar';
import EmailUnverifiedRoute from 'src/view/shared/routes/EmailUnverifiedRoute';
import EmptyPermissionsRoute from 'src/view/shared/routes/EmptyPermissionsRoute';
import EmptyTenantRoute from 'src/view/shared/routes/EmptyTenantRoute';
import PrivateRoute from 'src/view/shared/routes/PrivateRoute';
import PublicRoute from 'src/view/shared/routes/PublicRoute';

interface BaseRoute {
  path: string;
  component?: ComponentType<any>;
  loader?: () => Promise<{ default: ComponentType<any> }>;
  exact?: boolean;
  permissionRequired?: keyof typeof Permissions.values;
}

interface Routes {
  privateRoutes: BaseRoute[];
  publicRoutes: BaseRoute[];
  emptyTenantRoutes: BaseRoute[];
  emptyPermissionsRoutes: BaseRoute[];
  emailUnverifiedRoutes: BaseRoute[];
  simpleRoutes: BaseRoute[];
}

interface RoutesComponentProps {
  routes: Routes;
}

function RoutesComponent({ routes }: RoutesComponentProps) {
  const isInitialMount = useRef(true);
  const [menus, setMenus]: any = useState(
    staticMenus || [],
  );

  const authLoading = useSelector(
    authSelectors.selectLoadingInit,
  );
  const layoutLoading = useSelector(
    layoutSelectors.selectLoading,
  );
  const loading = authLoading || layoutLoading;
  const currentUser = useSelector(
    authSelectors.selectCurrentUser,
  );
  const currentTenant = useSelector(
    authSelectors.selectCurrentTenant,
  );

  useEffect(() => {
    console.log('Auth State:', {
      loading,
      authLoading,
      layoutLoading,
      currentUser,
      currentTenant,
    });
  }, [
    loading,
    authLoading,
    layoutLoading,
    currentUser,
    currentTenant,
  ]);

  const getStudioDashboards = async () => {
    setMenus(staticMenus);

    const data = await DataStudioService.list(
      {
        showInMenu: true,
        getStudioDashboards: true,
      },
      'position',
      null,
      0,
    );

    const dashMenus = data.rows?.map((row) => ({
      path: `/dashboard-studio/${row.id}`,
      exact: true,
      icon: <PieChart />,
      label: row.name,
      permissionRequired: null,
      backgroundColor:
        row.additionalAttributes?.backgroundColor?.main,
      color: row.additionalAttributes?.color?.main,
    }));

    const newMenus = [
      ...staticMenus.slice(0, 1),
      ...dashMenus,
      ...staticMenus.slice(1),
    ];

    setMenus(newMenus);
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      ProgressBar.start();
      return;
    }

    if (!loading) {
      ProgressBar.done();
    }
  }, [loading]);

  useEffect(() => {
    if (!currentTenant) return;
    getStudioDashboards();
  }, [loading, currentTenant]);

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-lg font-medium">
            Loading...
          </div>
          <div className="text-sm text-muted-foreground">
            Please wait while we initialize the application
          </div>
        </div>
      </div>
    );
  }

  const renderRoute = (route: BaseRoute) => {
    const {
      path,
      component: Component,
      loader,
      permissionRequired,
    } = route;

    if (permissionRequired) {
      const permissionChecker = new PermissionChecker(
        currentTenant,
        currentUser,
      );

      if (
        !permissionChecker.match(
          Permissions.values[permissionRequired],
        )
      ) {
        return null;
      }
    }

    const LoadableComponent = loader
      ? CustomLoadable({ loader })
      : null;

    if (!Component && !LoadableComponent) {
      return null;
    }

    const RouteComponent =
      Component ||
      (LoadableComponent as React.ComponentType<any>);

    // Wrap private routes with PrivateRoute component
    if (routes.privateRoutes.includes(route)) {
      return (
        <Route
          key={path}
          path={path}
          element={
            <PrivateRoute
              component={RouteComponent}
              currentTenant={currentTenant}
              currentUser={currentUser}
              menus={menus}
            />
          }
        />
      );
    }

    // Wrap public routes with PublicRoute component
    if (routes.publicRoutes.includes(route)) {
      return (
        <Route
          key={path}
          path={path}
          element={
            <PublicRoute
              component={RouteComponent}
              currentTenant={currentTenant}
              currentUser={currentUser}
            />
          }
        />
      );
    }

    // Wrap email unverified routes
    if (routes.emailUnverifiedRoutes.includes(route)) {
      return (
        <Route
          key={path}
          path={path}
          element={
            <EmailUnverifiedRoute
              component={RouteComponent}
              currentTenant={currentTenant}
              currentUser={currentUser}
            />
          }
        />
      );
    }

    // Wrap empty tenant routes
    if (routes.emptyTenantRoutes.includes(route)) {
      return (
        <Route
          key={path}
          path={path}
          element={
            <EmptyTenantRoute
              component={RouteComponent}
              currentTenant={currentTenant}
              currentUser={currentUser}
            />
          }
        />
      );
    }

    // Wrap empty permissions routes
    if (routes.emptyPermissionsRoutes.includes(route)) {
      return (
        <Route
          key={path}
          path={path}
          element={
            <EmptyPermissionsRoute
              component={RouteComponent}
              currentTenant={currentTenant}
              currentUser={currentUser}
            />
          }
        />
      );
    }

    // Simple routes don't need wrapping
    return (
      <Route
        key={path}
        path={path}
        element={<RouteComponent />}
      />
    );
  };

  return (
    <Routes>
      {routes.privateRoutes?.map((route: BaseRoute) =>
        renderRoute(route),
      )}
      {routes.publicRoutes?.map((route: BaseRoute) =>
        renderRoute(route),
      )}
      {routes.emailUnverifiedRoutes?.map(
        (route: BaseRoute) => renderRoute(route),
      )}
      {routes.emptyTenantRoutes?.map((route: BaseRoute) =>
        renderRoute(route),
      )}
      {routes.emptyPermissionsRoutes?.map(
        (route: BaseRoute) => renderRoute(route),
      )}
      {routes.simpleRoutes?.map((route: BaseRoute) =>
        renderRoute(route),
      )}
    </Routes>
  );
}

export default RoutesComponent;
